<template lang="pug">
  .b-spec-cell(:class="{ editable: editable && !readOnly, active }" @click="onClick")
    textarea._input(
      v-if="active"
      ref="input"
      rows="1"
      data-testid="spec-cell-input"
      :value="draft"
      @input="onInput"
      @keydown.enter.prevent="save"
      @keydown.esc.prevent="cancel"
      @blur="save"
      )
    template(v-else)
      ._value {{ display }}
      slot
</template>

<script>
/**
 * Ячейка таблицы спецификации с правкой по клику (ТЗ 6.2): текст — по клику, сохранение по
 * Enter или потере фокуса; числа отдаются как введены, форматирование — забота родителя. Поле
 * ввода многострочное с переносом слов: открывается высотой текста ячейки и растёт по мере ввода.
 *
 * Поле ввода существует ровно одно на экране, поэтому «какая ячейка сейчас правится» решает
 * таблица и передаёт сюда `active` — иначе метка spec-cell-input перестала бы быть единственной.
 * Сгенерировано Claude.
 */
export default {
  name: 'SpecCell',
  props: {
    value: [String, Number],
    /** Готовый текст для показа (деньги, количество, срок словами). */
    display: String,
    editable: { type: Boolean, default: true },
    readOnly: Boolean,
    active: Boolean,
  },
  data() {
    return {
      draft: '',
      // защита от двойного сохранения: Enter и последующая потеря фокуса — одно событие правки
      saving: false,
      /** Высота текста ячейки до начала правки: поле ввода не бывает ниже неё. */
      textHeight: 0,
    };
  },
  watch: {
    active(val) {
      if (!val) return;
      // наблюдатель срабатывает до перерисовки — ячейка ещё показывает текст, его высоту и берём
      this.textHeight = this.$el.offsetHeight;
      this.saving = false;
      this.draft = this.value === null || this.value === undefined ? '' : String(this.value);
      this.$nextTick(() => {
        const { input } = this.$refs;
        if (!input) return;
        this.fitHeight();
        input.focus();
      });
    },
  },
  methods: {
    onInput(event) {
      this.draft = event.target.value;
      this.fitHeight();
    },
    /** Высота поля — по тексту, но не ниже исходного текста ячейки: строка таблицы не прыгает. */
    fitHeight() {
      const { input } = this.$refs;
      if (!input) return;
      input.style.height = 'auto';
      const border = input.offsetHeight - input.clientHeight;
      input.style.height = `${Math.max(input.scrollHeight + border, this.textHeight)}px`;
    },
    onClick() {
      if (!this.editable || this.readOnly || this.active) return;
      this.$emit('edit');
    },
    save() {
      if (!this.active || this.saving) return;
      this.saving = true;
      // значения ячеек однострочные: перенос — только визуальный, вставленные разрывы строк — пробелы
      const next = this.draft.replace(/\s*\n\s*/g, ' ').trim();
      const prev = this.value === null || this.value === undefined ? '' : String(this.value);
      this.$emit('close');
      if (next === prev) return;
      this.$emit('save', next);
    },
    cancel() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.spec-cell {
  display: block;
  min-height: 20px;
  line-height: 20px;
  word-break: break-word;

  &.editable {
    cursor: text;

    &:hover {
      box-shadow: inset 0 0 0 1px var(--color-purple);
      border-radius: 3px;
    }
  }

  // поле повторяет текст ячейки: та же ширина и высота строки, рамка тенью и отступы за счёт
  // отрицательных полей — переносы слов совпадают, и высота поля равна высоте текста
  &__input {
    width: calc(100% + 8px);
    margin: 0 -4px;
    box-sizing: border-box;
    border: none;
    box-shadow: inset 0 0 0 1px var(--color-accent);
    border-radius: 3px;
    padding: 0 4px;
    font: inherit;
    line-height: 20px;
    display: block;
    resize: none;
    overflow: hidden;
    word-break: break-word;
  }

  &__value {
    display: inline;
  }
}
</style>
